import { Button } from "@/modules/shadcn/ui/button";
import { AutoAnnotation, PreAnnotateProviderEnum } from "@repo/schema";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { tKey, useFormatRelative, type I18nKey } from "@/core/i18n";

export interface AwaitingReviewBannerProps {
  autoAnnotations: AutoAnnotation[];
  /** The run found nothing: no rows to show, but the image still awaits a look. */
  emptyRun?: boolean;
  onApprove: () => void;
  onDismiss: () => void;
  isSaving?: boolean;
}

const PROVIDER_LABEL = {
  [PreAnnotateProviderEnum.ROBOPIPE_VISION]: tKey("label:provider.robopipe_vision"),
  [PreAnnotateProviderEnum.TRAINED_MODEL]: tKey("label:provider.trained_model"),
  [PreAnnotateProviderEnum.SAM3]: tKey("label:provider.sam3"),
} satisfies Record<PreAnnotateProviderEnum, I18nKey<"label">>;

/**
 * "These boxes came from a model and nobody has checked them yet."
 *
 * Shown whenever the image carries staged auto-annotations — including, and
 * mostly, long after the run that produced them: the labeller can start a run,
 * go and do something else, and find this waiting when they come back.
 *
 * Approve is the primary action and is the only way the predictions become
 * real annotations: it saves the image with the staged boxes promoted, so
 * whatever survives on the canvas is what the human approved. A plain Save
 * keeps them staged. Dismiss only puts the banner away — the predictions stay
 * staged, and the header's Approve button still offers to commit them.
 */
export const AwaitingReviewBanner = ({
  autoAnnotations,
  emptyRun = false,
  onApprove,
  onDismiss,
  isSaving,
}: AwaitingReviewBannerProps) => {
  const { t } = useTranslation(["label", "common"]);
  const formatRelative = useFormatRelative();
  const count = autoAnnotations.length;
  if (count === 0 && !emptyRun) return null;

  // Every row of one staged set shares a run, so the first carries the whole
  // set's provenance. An empty run has no rows, hence no provenance to show.
  const [first] = autoAnnotations;
  // A provider value this build has no label for shows as it arrived.
  const providerKey: I18nKey<"label"> | undefined = first && PROVIDER_LABEL[first.provider];
  const provider = first && (providerKey ? t(providerKey) : first.provider);
  const when = first && formatRelative(first.createdAt);

  return (
    <div
      data-slot="awaiting-review-banner"
      className="pointer-events-none absolute inset-x-3 bottom-[84px] z-20 flex justify-center"
    >
      <div className="pointer-events-auto flex w-[min(460px,100%)] items-center gap-3 rounded-xl border border-violet-200 bg-white/95 px-3 py-2.5 shadow-lg backdrop-blur animate-in fade-in slide-in-from-bottom-2 duration-200">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-700">
          <Sparkles className="size-4" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[13px] font-semibold leading-4 text-foreground/90">
            {count === 0 ? t("awaitingReview.emptyTitle") : t("awaitingReview.title", { count })}
          </span>
          <span className="truncate text-[11px] leading-4 text-foreground/60">
            {count === 0 ? t("awaitingReview.emptyHint") : `${provider} · ${when}`}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          title={t("awaitingReview.dismissTitle")}
        >
          {t("actions.dismiss")}
        </Button>
        <Button
          size="sm"
          onClick={onApprove}
          disabled={isSaving}
          className="bg-emerald-500 hover:bg-emerald-600"
        >
          {count === 0 ? t("canvas.noObjects") : t("actions.approve")}
        </Button>
      </div>
    </div>
  );
};
